import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import * as sessionService from '../../services/sessionService';
import { useTheme } from '../../contexts/ThemeContext';

interface CheckpointControlsProps {
  sessionId: string;
  checkpointId: string;
}

export const CheckpointControls: React.FC<CheckpointControlsProps> = ({
  sessionId,
  checkpointId
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  
  const rollbackMutation = useMutation(
    () => sessionService.rollbackToCheckpoint(sessionId, checkpointId),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['session', sessionId]);
        // Navigate to the new restored session if applicable
        if (data && data.new_session_id) {
          navigate(`/sessions/${data.new_session_id}`);
        } else {
          alert('Session rolled back successfully');
        }
      },
      onError: (error) => {
        console.error('Failed to rollback session:', error);
        alert('Failed to rollback session');
      }
    }
  );
  
  const handleRollback = () => {
    if (confirm('Are you sure you want to roll back to this checkpoint? This will create a new session from this checkpoint.')) {
      rollbackMutation.mutate();
    }
  };
  
  const handleExport = () => {
    sessionService.exportCheckpoint(sessionId, checkpointId)
      .catch(error => {
        console.error('Failed to export checkpoint:', error);
        alert('Failed to export checkpoint');
      });
  };
  
  return (
    <div className="checkpoint-controls space-y-2">
      <button
        className="w-full px-3 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 flex items-center justify-center"
        onClick={handleRollback}
        disabled={rollbackMutation.isLoading}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1z" clipRule="evenodd" />
        </svg>
        {rollbackMutation.isLoading ? 'Restoring...' : 'Restore from this checkpoint'}
      </button>
      
      <button
        className={`w-full px-3 py-2 text-white rounded flex items-center justify-center ${
          darkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-600 hover:bg-gray-700'
        }`}
        onClick={handleExport}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
        Export Checkpoint
      </button>
    </div>
  );
};
